'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleActions, BubbleRows } from '@/components/chat/BubbleBlock';
import { SendPreview } from '@/components/chat/blocks/SendPreview';
import { decideFromChat } from '@/lib/chat/block-actions';
import {
  isNoLongerWaiting,
  stackSummary,
  stackSummaryNote,
  type ApprovalStackBlock,
  type StackOutcome,
} from '@/lib/chat/blocks/approval-stack';

/**
 * "Anything need me?" — the waiting cards, one at a time (approval-stack.ts).
 *
 * Each card says what approving it does, who it reaches and the words, through
 * the shared `SendPreview`, so this approval reads exactly like every other
 * one in the chat. The orange verb decides the EXISTING card; "Not now"
 * snoozes it, so it comes back. A card that cannot be approved from a screen
 * (money, the books, settings, roll-ups, big batches) is a row into Actions and
 * a quiet Next, never a button.
 *
 * A refusal stays on the card in the server's own words (#35, inline per #42).
 * When those words say the card is no longer waiting — decided elsewhere since
 * this reply was written — the card offers only Next: trying again is
 * pointless, and putting off a decided card would wake it.
 */
export interface ApprovalStackProps {
  block: ApprovalStackBlock;
  /** False on an older reply, or while Mucka is mid-answer: taps are off. */
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

export function ApprovalStack({ block, live, onOpen }: ApprovalStackProps) {
  const [outcomes, setOutcomes] = useState<StackOutcome[]>([]);
  const [snoozing, setSnoozing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState<string | null>(null);

  const total = block.cards.length;
  const at = outcomes.length;
  const card = at < total ? block.cards[at] : null;

  const advance = (outcome: StackOutcome) => {
    setOutcomes(prev => [...prev, outcome]);
    setSnoozing(false);
    setError(null);
    setGone(null);
  };

  const approve = async (actionId: string) => {
    setError(null);
    const out = await decideFromChat(actionId, 'approved');
    if (out.ok) advance('approved');
    else if (isNoLongerWaiting(out.message)) setGone(out.message);
    return out;
  };

  const putOff = async (actionId: string) => {
    setSnoozing(true);
    setError(null);
    const out = await decideFromChat(actionId, 'snoozed');
    if (out.ok) {
      advance('put-off');
      return;
    }
    setSnoozing(false);
    if (isNoLongerWaiting(out.message)) setGone(out.message);
    else setError(out.message);
  };

  return (
    <div className="mchat-stack" role="group" aria-label={`${total + block.more} card${total + block.more === 1 ? '' : 's'} waiting`}>
      <div className="mchat-stack-dots" aria-hidden="true">
        {block.cards.map((c, i) => {
          const o = outcomes[i];
          const state = o === 'approved' ? 'approved' : o ? 'passed' : i === at ? 'current' : 'ahead';
          return <span key={c.actionId} className={`mchat-stack-dot mchat-stack-dot--${state}`} />;
        })}
      </div>

      {card ? (
        <div className="mchat-stack-card" key={card.actionId}>
          <div className="mchat-stack-head">
            {card.tag && <span className="mchat-stack-tag">{card.tag}</span>}
            {card.isDemo && <span className="mchat-stack-demo">Example card</span>}
            <span className="mchat-stack-pos">{at + 1} of {total}</span>
          </div>
          <p className="mchat-stack-title">{card.title}</p>

          {gone ? (
            <>
              <p className="mchat-body mchat-error" role="alert">{gone}</p>
              <p className="mchat-stack-note">It isn&apos;t waiting any more, so there is nothing to do with it here.</p>
              <BubbleActions actions={[{ label: 'Next', tone: 'quiet', disabled: !live, onPress: () => advance('gone') }]} />
            </>
          ) : card.approvable ? (
            <>
              {error && <p className="mchat-body mchat-error" role="alert">{error}</p>}
              <SendPreview
                key={card.actionId}
                what={card.what}
                to={card.to}
                words={card.words}
                verb={card.verb}
                doneLabel="Done"
                laterLabel={snoozing ? 'Putting off…' : 'Not now'}
                onApprove={() => approve(card.actionId)}
                onLater={() => { void putOff(card.actionId); }}
                live={live && !snoozing}
              />
            </>
          ) : (
            <>
              <p className="mchat-stack-note">This one is approved in Actions, where you can see all of it first.</p>
              <BubbleRows
                rows={[{ id: `open:${card.actionId}`, title: 'Approve this one in Actions', kind: 'go', onPress: () => onOpen(card.path) }]}
              />
              <BubbleActions actions={[{ label: 'Next', tone: 'quiet', disabled: !live, onPress: () => advance('left') }]} />
            </>
          )}
        </div>
      ) : (
        <div className="mchat-stack-summary" aria-live="polite">
          <span className="mchat-stack-summary-line">
            <Icon icon={Check} size={16} />
            {stackSummary(outcomes)}
          </span>
          {stackSummaryNote(outcomes) && <p className="mchat-stack-note">{stackSummaryNote(outcomes)}</p>}
        </div>
      )}

      {block.more > 0 && (
        <BubbleRows
          rows={[{ id: 'more', title: `${block.more} more in Actions`, kind: 'go', onPress: () => onOpen('/actions') }]}
        />
      )}
    </div>
  );
}
